// src/runner.js
import { logger } from "./Logger.js";
import { stableHash } from "./coreRepoJSONL.js";
import { PerkSystem } from "./PerkSystem.js";
import { QuestSystem } from "./QuestSystem.js";

export class GuardRunner {
  constructor(coreRepo, passiveSystem) {
    this.coreRepo = coreRepo;
    this.passiveSystem = passiveSystem;
    this.currentSession = null;
  }

  // Devuelve el nodo actual de la sesión para renderizado [NEW]
  getNextEncounter() {
    if (!this.currentSession) return null;
    const { encounters, currentIndex } = this.currentSession;
    if (currentIndex >= encounters.length) return null;
    
    const nodeId = encounters[currentIndex];
    return this.coreRepo.getNode(nodeId);
  }

  // Prepara la guardia eligiendo los nodos, pero NO los ejecuta [cite: 5, 9]
  async prepareShift(query = {}, userProfile = {}) {
    let nodeIds;

    // Switch Strategy: Direct Query (Library) vs. Smart Generation (Ranked/Specialty)
    if (query.ids) {
      // Legacy/Direct Mode
      nodeIds = await this.coreRepo.selectNodeIds(query);
      this.currentSpecialty = 'Guardia General';
    } else {
      // Smart Generation Mode (The Director AI)
      // Now supports tags/difficulty filters for specialty-specific sagas
      const generated = await this.coreRepo.generateSessionDeck(userProfile, 'RANKED', query);
      nodeIds = generated.deck;
      this.currentSpecialty = generated.specialty;
    }

    const selected = this.selectEncounters(nodeIds, userProfile, 3); // Regla: 3 encuentros por guardia

    // [INTEGRATION] Log active passives for debugging/feedback
    if (this.passiveSystem) {
      logger.debug("Active Passives:", this.passiveSystem.passives);
    }

    // Compute modifiers early so they're available for session setup
    this.rpgProfile = userProfile.rpgProfile || null;
    this.modifiers = PerkSystem.getModifiers(this.rpgProfile);
    const stabilityBonus = this.modifiers?.guard?.stabilityBonusStart || 0;

    this.currentSession = {
      session_id: `guard_${Date.now()}`,
      specialty: this.currentSpecialty,
      encounters: selected,
      isChain: selected.length === 1,
      results: [],
      hashes: [],
      currentIndex: 0,
      patientState: {
        estabilidad: Math.min(115, 100 + stabilityBonus),
        risk: 0, // 0 a 100, donde >80 es riesgo inminente
        flags: [], // Secuelas o modificadores activos (ej. "hipotensión_crítica")
        metrics: {
          precision: { correct: 0, total: 0 },
          velocidad: { totalTime: 0, count: 0 },
          seguridad: 100, // Penalizado fuertemente por errores letales o graves
          eficiencia: 100 // Penalizado por errores frecuentes/diagnósticos
        },
        vitals: {
          ta_sys: 120,
          ta_dia: 80,
          fc: 80,
          satO2: 98,
          mental: "Alerta"
        }
      },
      questionStartTime: Date.now()
    };
    this.activeSkillUsed = false;

    return this.currentSession;
  }

  // Ejecuta la habilidad activa del mentor equipado
  async useActiveSkill(skillId) {
    if (!this.currentSession) return null;
    
    // Track usage per session
    this.currentSession.skillsUsed = this.currentSession.skillsUsed || {};
    if (this.currentSession.skillsUsed[skillId]) return null;

    const nodeId = this.currentSession.encounters[this.currentSession.currentIndex];
    const node = this.coreRepo.getNode(nodeId);
    if (!node) return null;

    this.currentSession.skillsUsed[skillId] = true;
    const res = { success: true, refreshOptions: false };

    switch (skillId) {
      case 'antigrav_time':
        // Dr. Antigravité: Estabilización temporal (+20 segundos extra inmediatos)
        res.effect = 'add_time';
        break;

      case 'intuicion_lunar': {
        // Dra. Luna: Revela si el caso es quirúrgico o médico, y sana 15% de estabilidad
        this.currentSession.patientState.estabilidad = Math.min(115, this.currentSession.patientState.estabilidad + 15);
        const nodeTags = (node.tags || []).map(t => t.toLowerCase());
        if (node.topic) nodeTags.push(node.topic.toLowerCase());
        if (node.tema) nodeTags.push(node.tema.toLowerCase());
        
        const isSurgical = nodeTags.some(t => 
            t.includes('cirug') || t.includes('quir') || t.includes('trauma') || t.includes('apendic') || t.includes('obstetr') || t.includes('ginec')
        );
        res.effect = 'reveal_reasoning';
        res.reasoning = [
            `🔮 Intuición Lunar: Este es un caso eminentemente ${isSurgical ? 'QUIRÚRGICO' : 'MÉDICO'}.`,
            `Estabilidad del paciente restaurada en +15%.`
        ];
        break;
      }

      case 'diagrama_feynman':
        // Dr. Feynman: Reduce costo de fatiga a la mitad de forma permanente para la guardia
        this.currentSession.feynmanActive = true;
        res.effect = 'reveal_reasoning';
        res.reasoning = ["📊 Diagrama de Flujo: El costo de fatiga (burnout) por errores se reduce a la mitad por el resto de esta guardia."];
        break;

      case 'brillo_radiante':
        // Dra. Marie Curie: Sana 20 de estabilidad y revela especialidad
        this.currentSession.patientState.estabilidad = Math.min(115, this.currentSession.patientState.estabilidad + 20);
        this.currentSession.revealTags = true;
        res.effect = 'reveal_reasoning';
        res.reasoning = [
            `✨ Brillo Radiante: Estabilidad del paciente restaurada en +20.`,
            `Especialidad del caso revelada: ${node.topic || node.tema || 'General'}.`
        ];
        break;

      case 'proporcion_aurea':
        // Dr. Da Vinci: Doble créditos en la siguiente correcta
        this.currentSession.doubleCreditsNextCorrect = true;
        res.effect = 'reveal_reasoning';
        res.reasoning = ["📐 Proporción Áurea: La siguiente respuesta correcta otorgará +50 créditos de bono extra al finalizar la guardia."];
        break;

      case 'metodo_deductivo': {
        // Dr. Watson: Método Deductivo — te dice cuál es la opción más larga
        let longestOpt = node.opciones[0];
        node.opciones.forEach(o => {
            if (o.texto.length > longestOpt.texto.length) {
                longestOpt = o;
            }
        });
        res.effect = 'reveal_reasoning';
        res.reasoning = [
            `🔍 Método Deductivo: Observo que la opción "${longestOpt.texto.slice(0, 30)}..." es la más larga.`,
            `Estadísticamente en el ENARM, las respuestas correctas contienen mayor precisión y detalle.`
        ];
        break;
      }

      case 'cafe_cargado':
        // Dra. Pérez: Restaura fatiga de forma inmediata
        res.effect = 'reduce_burnout';
        res.burnoutDelta = -15;
        break;

      case 'binary_search':
        // Dra. Schwerkraftfrei: Eliminación lógica (50/50 + ¿Por qué?)
        const correctBin = node.opciones.find(o => o.resultado === 'correcto');
        const wrongsBin = node.opciones.filter(o => o.resultado !== 'correcto');
        wrongsBin.sort(() => Math.random() - 0.5).slice(0, 2).forEach(o => o._hidden = true);
        
        res.refreshOptions = true;
        res.effect = 'reveal_elimination';
        res.logic = "He eliminado las opciones fisiopatológicamente improbables.";
        break;

      case 'neural_refresh':
        // Dr. Neyron-A: Reduce Burnout cognitivo
        res.effect = 'reduce_burnout';
        res.burnoutDelta = -25;
        break;

      case 'quantum_eval':
        // MIP Kvantovaya: Razonamiento Cuántico (Pedagogía Inversa) [cite: 5, 10]
        // En lugar de dar la correcta, damos pistas sobre por qué las otras fallan
        res.effect = 'reveal_reasoning';
        res.reasoning = node.opciones
            .filter(o => o.resultado !== 'correcto' && o.retro)
            .map(o => `⚠️ ${o.texto.slice(0,30)}...: ${o.retro || 'No recomendado.'}`)
            .sort(() => Math.random() - 0.5)
            .slice(0, 2);
        break;

      case 'reanimacion':
        this.currentSession.ignoreOneLethal = true;
        res.effect = 'ignore_lethal';
        break;

      case 'ojo_clinico': {
        // Internista: Descarte Razonado — elimina 1 distractor incorrecto
        const wrongsOjo = node.opciones.filter(o => o.resultado !== 'correcto' && !o._hidden);
        if (wrongsOjo.length > 0) {
          wrongsOjo[Math.floor(Math.random() * wrongsOjo.length)]._hidden = true;
        }
        res.refreshOptions = true;
        res.effect = 'reveal_elimination';
        res.logic = "Descarté la opción menos compatible con el cuadro clínico.";
        break;
      }

      case 'corte_preciso': {
        // Cirujano: Decisión Crítica — estabiliza y revela el razonamiento de un distractor
        this.currentSession.patientState.estabilidad = Math.min(115, this.currentSession.patientState.estabilidad + 10);
        res.effect = 'reveal_reasoning';
        res.reasoning = node.opciones
            .filter(o => o.resultado !== 'correcto' && o.retro)
            .map(o => `⚠️ ${o.texto.slice(0, 30)}...: ${o.retro}`)
            .sort(() => Math.random() - 0.5)
            .slice(0, 1);
        if (res.reasoning.length === 0) {
          res.reasoning = ["Actúa con decisión: prioriza la conducta que estabiliza al paciente."];
        }
        break;
      }

      case 'basic_advice':
        // Dr. Hernández: Segunda Opinión (Pista simple)
        res.effect = 'reveal_reasoning';
        res.reasoning = [this.coreRepo.getNode(nodeId)?.perla || "Analiza bien los signos vitales, Resident."];
        break;

      default:
        delete this.currentSession.skillsUsed[skillId];
        return null;
    }

    return res;
  }

  // Registra una decisión real del usuario y devuelve el resultado [cite: 9, 10]
  async processDecision(decisionId) {
    const session = this.currentSession;
    const nodeId = session.encounters[session.currentIndex];

    // Evaluación clínica real desde el CORE
    const coreRes = await this.coreRepo.evaluate(nodeId, decisionId);

    // Actualizar patientState basado en la decisión
    this.applyConsequences(coreRes, session);

    // [RPG] Urgenciologist Skill: Reanimación
    if (coreRes.isLethal && session.ignoreOneLethal) {
      logger.info("RPG Skill Triggered: Reanimación saved the day.");
      coreRes.isLethal = false;
      coreRes.retro += " (SALVADO POR REANIMACIÓN)";
      // Restore minimum stability AND vitals from collapse state
      session.patientState.estabilidad = Math.max(10, session.patientState.estabilidad);
      session.patientState.vitals.ta_sys = Math.max(80, session.patientState.vitals.ta_sys);
      session.patientState.vitals.ta_dia = Math.max(50, session.patientState.vitals.ta_dia);
      session.patientState.vitals.fc = Math.min(120, session.patientState.vitals.fc);
      session.patientState.vitals.satO2 = Math.max(88, session.patientState.vitals.satO2);
      session.patientState.vitals.mental = "Somnoliento";
      session.ignoreOneLethal = false; // Consumed
    }

    // [NARRATIVE] Support for branching/sequences
    const node = this.coreRepo.getNode?.(nodeId);
    let chainNextNode = null;
    if (node && node.opciones) {
        for (const opt of node.opciones) {
            if (opt.next_node_id) {
                chainNextNode = opt.next_node_id;
                break;
            }
        }
    }
    
    if (chainNextNode) {
      logger.debug(`Narrative Flow: Injecting next node ${chainNextNode} (Chain continuation)`);
      session.encounters.splice(session.currentIndex + 1, 0, chainNextNode);
    } else if (coreRes.next_node_id) {
      logger.debug(`Narrative Flow: Injecting next node ${coreRes.next_node_id}`);
      session.encounters.splice(session.currentIndex + 1, 0, coreRes.next_node_id);
    }

    const timeElapsed = Date.now() - session.questionStartTime;
    session.questionStartTime = Date.now(); // reset para la siguiente

    // Calculate time metrics (ideal: < 15s per question = perfect speed)
    session.patientState.metrics.velocidad.count += 1;
    session.patientState.metrics.velocidad.totalTime += timeElapsed;
    const isSlow = timeElapsed > 25000;

    session.results.push({
      node_id: nodeId,
      decision_id: decisionId,
      result: coreRes.result,
      stability_after: session.patientState.estabilidad,
      isLethal: coreRes.isLethal,
      timeElapsed,
      isSlow
    });
    session.hashes.push(coreRes.clinical_outcomes_hash);
    session.currentIndex++;
    
    // [F2.3] Resetear vitales si el siguiente paciente no es parte de una cadena
    if (!chainNextNode && !coreRes.next_node_id && session.currentIndex < session.encounters.length) {
       const stabilityBonus = this.modifiers?.guard?.stabilityBonusStart || 0;
       session.patientState.estabilidad = Math.min(115, 100 + stabilityBonus);
       session.patientState.risk = 0;
       session.patientState.flags = [];
       session.patientState.vitals = {
          ta_sys: 120,
          ta_dia: 80,
          fc: 80,
          satO2: 98,
          mental: "Alerta"
       };
    }

    return coreRes;
  }

  // Aplica las secuelas o beneficios al estado del paciente
  applyConsequences(coreRes, session) {
    const pState = session.patientState;
    pState.metrics.precision.total += 1;

    let dmgMultiplier = 1;
    if (this.rpgProfile && this.rpgProfile.classId === 'cirujano') {
      dmgMultiplier = 1.5; // Los cirujanos son más decisivos pero sus errores cuestan más estabilidad
    }

    const mitigation = this.modifiers?.guard?.stabilityMitigationGlobal || 0;

    if (coreRes.result === 'correct') {
      pState.metrics.precision.correct += 1;
      
      let baseRecovery = 12;
      if (this.passiveSystem) {
        baseRecovery = this.passiveSystem.applyStabilityRecoveryMultiplier(baseRecovery);
      }
      
      pState.estabilidad = Math.min(100, pState.estabilidad + baseRecovery);
      pState.risk = Math.max(0, pState.risk - 15);
      
      // Normalización progresiva de signos vitales
      if (pState.vitals.fc > 85) pState.vitals.fc -= Math.floor(Math.random() * 8) + 2;
      if (pState.vitals.fc < 65) pState.vitals.fc += Math.floor(Math.random() * 5);
      if (pState.vitals.satO2 < 98) pState.vitals.satO2 = Math.min(100, pState.vitals.satO2 + 1);
      if (pState.vitals.ta_sys < 115) pState.vitals.ta_sys += 5;
      if (pState.vitals.ta_sys > 135) pState.vitals.ta_sys -= 5;
      if (pState.vitals.ta_dia < 70) pState.vitals.ta_dia += 3;
      if (pState.vitals.ta_dia > 90) pState.vitals.ta_dia -= 3;
      if (pState.vitals.mental === "Estupor" || pState.vitals.mental === "Somnoliento") pState.vitals.mental = "Alerta";
    } else {
      // Determinar gravedad de reducción
      const isLethal = coreRes.isLethal || coreRes.result === 'error_letal';
      
      if (isLethal) {
        if (mitigation > 0) {
          // Pierde solo 80% de estabilidad (mantiene 20%)
          pState.estabilidad = Math.round(pState.estabilidad * 0.2);
        } else {
          pState.estabilidad = 0;
        }
        pState.risk = 100;
        pState.metrics.seguridad = Math.max(0, pState.metrics.seguridad - 60);
        pState.flags.push("evento_sentinela");
        
        // Colapso hemodinámico
        pState.vitals.ta_sys = Math.max(40, pState.vitals.ta_sys - 55);
        pState.vitals.ta_dia = Math.max(20, pState.vitals.ta_dia - 35);
        pState.vitals.fc = Math.min(190, pState.vitals.fc + 65);
        pState.vitals.satO2 = Math.max(40, pState.vitals.satO2 - 35);
        pState.vitals.mental = "Coma";
      } else if (coreRes.rawOutcome === 'error_grave') {
        let dmg = 25 * dmgMultiplier;
        if (this.passiveSystem) {
          dmg = this.passiveSystem.applyStabilityMitigation(dmg);
        }
        if (mitigation > 0) {
          dmg = dmg * (1 - mitigation);
        }
        pState.estabilidad -= Math.round(dmg);
        pState.risk += 25;
        pState.metrics.seguridad -= 20;
        pState.flags.push("complicacion_mayor");
        
        // Deterioro agudo
        pState.vitals.ta_sys -= 25;
        pState.vitals.fc += 35;
        pState.vitals.satO2 -= 12;
        pState.vitals.mental = "Estupor";
      } else {
        // error_frecuente o imprecisión menor
        let dmg = 15 * dmgMultiplier;
        if (this.passiveSystem) {
          dmg = this.passiveSystem.applyStabilityMitigation(dmg);
        }
        if (mitigation > 0) {
          dmg = dmg * (1 - mitigation);
        }
        pState.estabilidad -= Math.round(dmg);
        pState.risk += 15;
        pState.metrics.eficiencia -= 15;
        pState.flags.push("retraso_tratamiento");
        
        // Descompensación leve/moderada
        pState.vitals.ta_sys -= 12;
        pState.vitals.fc += 18;
        pState.vitals.satO2 -= 6;
        if (pState.vitals.mental === "Alerta") pState.vitals.mental = "Somnoliento";
      }
    }

    // Asegurar límites realistas
    pState.vitals.fc = Math.max(40, Math.min(220, pState.vitals.fc));
    pState.vitals.satO2 = Math.max(0, Math.min(100, pState.vitals.satO2));
    pState.vitals.ta_sys = Math.max(30, Math.min(240, pState.vitals.ta_sys));
    pState.vitals.ta_dia = Math.max(20, Math.min(140, pState.vitals.ta_dia));
    pState.estabilidad = Math.max(0, Math.min(115, pState.estabilidad));
  }

  // Genera el log final siguiendo la regla de hash acumulativo [cite: 3, 13]
  finalize() {
    const s = this.currentSession;

    // Calcular score final 0-100 para reportería
    const precisionScore = s.patientState.metrics.precision.total > 0
      ? Math.round((s.patientState.metrics.precision.correct / s.patientState.metrics.precision.total) * 100)
      : 0;

    // Velocidad: 100 menos penalización por promedio lento (>15s por pregunta)
    const avgTime = s.patientState.metrics.velocidad.count > 0
      ? s.patientState.metrics.velocidad.totalTime / s.patientState.metrics.velocidad.count
      : 0;
    const speedScore = Math.max(0, 100 - Math.max(0, (avgTime - 15000) / 500));

    return {
      session_id: s.session_id,
      timestamp: new Date().toISOString(),
      encounters: s.results,
      clinical_outcomes_hash: stableHash(s.hashes.join("|")), // Hash de integridad [cite: 3]
      energy_delta: -1,
      patientState: s.patientState,
      final_metrics: {
        precision: precisionScore,
        seguridad: Math.max(0, s.patientState.metrics.seguridad),
        eficiencia: Math.max(0, s.patientState.metrics.eficiencia),
        velocidad: Math.round(speedScore) // Mapear a tiempo real después
      },
      // [RPG] Otorgar afinidad si hubo un mentor activo
      mentor_affinity_gain: s.patientState.estabilidad > 0 ? 10 : 0,
      
      // Passives for rewards processing
      passiveSystem: this.passiveSystem
    };
  }

  selectEncounters(nodeIds, userProfile, total) {
    if (!Array.isArray(nodeIds) || nodeIds.length === 0) return [];
    const history = userProfile.history || {};
    const weakTags = new Set(userProfile.weakTags || []);
    const excludeIds = new Set(userProfile.excludeIds || []);

    // Identificar semillas de cadena en los candidatos
    const allNextNodeIds = new Set();
    const nodesWithNext = new Set();
    
    // Primero recopilamos todos los next_node_id de todo el repo para ser precisos
    if (this.coreRepo.nodeById) {
      for (const node of this.coreRepo.nodeById.values()) {
        if (!node.opciones) continue;
        for (const opt of node.opciones) {
          if (opt.next_node_id) allNextNodeIds.add(opt.next_node_id);
        }
      }
    }
    
    for (const id of nodeIds) {
      const node = this.coreRepo.getNode?.(id);
      if (!node || !node.opciones) continue;
      for (const opt of node.opciones) {
        if (opt.next_node_id) {
          nodesWithNext.add(id);
        }
      }
    }
    
    const chainSeeds = new Set([...nodesWithNext].filter(id => !allNextNodeIds.has(id)));
    const candidates = nodeIds.filter((id) => !excludeIds.has(id));

    const computeScore = (id) => {
      const node = this.coreRepo.getNode?.(id);
      if (!node) return 0;
      const lastSeen = history[id] || 0;
      const hoursSince = lastSeen ? (Date.now() - lastSeen) / 3600000 : 999;
      const freshnessScore = Math.min(3, hoursSince / 6);
      const tagHits = (node.tags || []).filter((tag) => weakTags.has(tag)).length;
      const difficultyBoost = userProfile.difficulty && node.dificultad === userProfile.difficulty ? 1 : 0;
      const randomness = Math.random();
      // Diversity Penalty: evaluated against already-chosen tags at selection time
      const diversityPenalty = (node.tags || []).some(t => chosenTags.has(t)) ? 0.3 : 1.0;
      
      let chainBoost = chainSeeds.has(id) ? 10 : 0;
      
      return (freshnessScore + tagHits * 1.5 + difficultyBoost + randomness + chainBoost) * diversityPenalty;
    };

    const chosen = [];
    const chosenTags = new Set();
    
    let isChain = false;

    while (chosen.length < total && candidates.length > 0) {
      let bestId = null;
      let bestScore = -1;
      for (const id of candidates) {
        if (chosen.includes(id)) continue;
        const score = computeScore(id);
        if (score > bestScore) { bestScore = score; bestId = id; }
      }
      if (!bestId) break;
      const bestNode = this.coreRepo.getNode?.(bestId);
      chosen.push(bestId);
      if (bestNode?.tags) bestNode.tags.forEach(t => chosenTags.add(t));
      
      if (chainSeeds.has(bestId)) {
          isChain = true;
          break; // Si elegimos una semilla, paramos. La cadena inyectará el resto.
      }
    }

    if (!isChain && chosen.length < total) {
      nodeIds.forEach((id) => {
        if (chosen.length < total && !chosen.includes(id)) {
          chosen.push(id);
        }
      });
    }

    return isChain ? chosen : chosen.slice(0, total);
  }
}